import React, { useState } from 'react';
import { ChevronDown, Map, PhoneCall } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageMetadata from '../hooks/usePageMetadata';
import { PRODUCT_RANGES, STORE } from '../config/store';
import { btnMd, btnPrimary, btnSm, containerWide, focusRing, heroPageHeading, sectionSpacing, btnSecondary } from '../styles/ui';

// Every answer below is grounded in information already published on the site:
// the product ranges from the homepage Categories section, the wholesale offer
// from the Wholesale section, and the address/phone from the Contact section.
// Opening hours, email and online ordering do not exist anywhere in this
// project, so the copy points to the phone instead of inventing them.
const FAQS = [
  {
    question: 'What products does Fresh & Homely offer?',
    answer: `We stock ${PRODUCT_RANGES.slice(0, -1).join(', ')} and ${PRODUCT_RANGES.slice(
      -1
    )}. Every cut is selected daily, cleaned and hygienically packed before it reaches you.`,
  },
  {
    question: 'Where is the store located?',
    answer: `You will find us at ${STORE.address}. Use the Get Directions link above to open the exact location in Google Maps.`,
  },
  {
    question: 'How can I contact the store?',
    answer: `The quickest way to reach us is by phone on ${STORE.phone}. You are also welcome to walk in and speak to our team directly.`,
  },
  {
    question: 'What are your opening hours?',
    answer: `Timings are not listed on this page, so please call us on ${STORE.phone} to confirm when we are open before you travel.`,
  },
  {
    question: 'Can I place an order online?',
    answer:
      'This website is a place to explore our range rather than an online shop, so there is no online ordering or checkout. For current availability, pricing or a specific requirement, please call or visit the store.',
  },
  {
    question: 'Do you support wholesale enquiries?',
    answer:
      'Yes. We supply fresh fish, dairy, meat and grocery essentials in bulk at wholesale rates, with regular supply and quality checks on every order.',
  },
  {
    question: 'How can businesses discuss bulk requirements?',
    answer: `Call us on ${STORE.phone} to talk through quantities, pricing and delivery schedules for your business.`,
  },
];

const SupportPage = () => {
  const [openItems, setOpenItems] = useState(() => new Set());

  usePageMetadata({
    title: 'Support | Fresh & Homely',
    description:
      'Get help from Fresh & Homely in Bengaluru. Call the store, find our address and directions, and read answers to common questions about our products and wholesale supply.',
    canonicalPath: '/support',
  });

  const toggleItem = (index) => {
    setOpenItems((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const quickHelp = [
    {
      icon: PhoneCall,
      title: 'Call Us',
      description: 'Speak to our team about products, availability or bulk orders.',
      linkLabel: STORE.phone,
      href: STORE.phoneHref,
      external: false,
    },
    {
      icon: Map,
      title: 'Get Directions',
      description: 'Open our exact location in Google Maps and navigate to the store.',
      linkLabel: 'Open in Google Maps',
      href: STORE.mapsUrl,
      external: true,
    },
  ];

  return (
    <>
      <Navbar />

      <main>
        {/* Support Hero */}
        <section className="hero-gradient pt-32 pb-16">
          <div className={containerWide}>
            <div className="max-w-3xl">
              <h1 className={`${heroPageHeading} mb-6`}>
                How can we help?
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Whether it is a question about our meat and seafood, where to find the store, or a
                wholesale requirement for your business, our team is happy to help. Reach us by
                phone or drop in — we would rather answer a question than have you guess.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Help */}
        <section className={`${sectionSpacing} bg-gradient-to-b from-white to-canvas`}>
          <div className={containerWide}>
            <div className="mb-12">
              <h2 className="serif text-3xl md:text-4xl text-emerald-950 mb-4">Quick help</h2>
              <p className="text-ink-secondary max-w-2xl">
                The fastest ways to get an answer or find us.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickHelp.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="card-hover bg-white p-6 rounded-3xl border border-emerald-100 shadow-card flex flex-col"
                  >
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-emerald-700" size={26} aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-950 mb-2">{item.title}</h3>
                    <p className="text-ink-secondary text-sm leading-relaxed mb-5 grow">
                      {item.description}
                    </p>
                    <a
                      href={item.href}
                      {...(item.external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      className={`${btnPrimary} ${btnSm}`}
                    >
                      {item.linkLabel}
                    </a>
                  </div>
                );
              })}

              {/* Visit Store — informational, so it carries the address rather than a CTA */}
              <div className="card-hover bg-emerald-50 p-6 rounded-3xl border border-emerald-100 shadow-soft flex flex-col">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4">
                  <Map className="text-emerald-700" size={26} aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-emerald-950 mb-2">Visit Store</h3>
                <address className="text-emerald-900 text-sm leading-relaxed not-italic">
                  {STORE.address}
                </address>
              </div>
            </div>
          </div>
        </section>

        {/* Common Questions */}
        <section className={`${sectionSpacing} hero-gradient`}>
          <div className={containerWide}>
            <div className="mb-12">
              <h2 className="serif text-3xl md:text-4xl text-emerald-950 mb-4">Common questions</h2>
              <p className="text-ink-secondary max-w-2xl">
                Answers to what customers ask us most often.
              </p>
            </div>

            <div className="max-w-3xl flex flex-col gap-4">
              {FAQS.map((faq, index) => {
                const isOpen = openItems.has(index);
                return (
                  <div
                    key={faq.question}
                    className="bg-white rounded-2xl border border-emerald-100 shadow-card overflow-hidden"
                  >
                    <h3>
                      <button
                        type="button"
                        onClick={() => toggleItem(index)}
                        aria-expanded={isOpen}
                        aria-controls={`faq-answer-${index}`}
                        id={`faq-question-${index}`}
                        className={`w-full flex items-center justify-between gap-4 text-left px-6 py-5 font-semibold text-emerald-950 hover:bg-emerald-50/60 transition-colors ${focusRing}`}
                      >
                        <span>{faq.question}</span>
                        <ChevronDown
                          size={20}
                          aria-hidden="true"
                          className={`text-emerald-700 shrink-0 transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </h3>
                    {isOpen && (
                      <div
                        id={`faq-answer-${index}`}
                        role="region"
                        aria-labelledby={`faq-question-${index}`}
                        className="px-6 pb-5 text-ink-secondary leading-relaxed"
                      >
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Need More Help */}
        <section className={`${sectionSpacing} bg-gradient-to-b from-white to-canvas`}>
          <div className={containerWide}>
            <div className="bg-white/90 backdrop-blur-sm border border-emerald-100 rounded-4xl shadow-floating p-8 md:p-12 text-center max-w-3xl mx-auto">
              <h2 className="serif text-3xl md:text-4xl text-emerald-950 mb-4">Still need help?</h2>
              <p className="text-ink-secondary mb-8 max-w-xl mx-auto leading-relaxed">
                If your question is not answered above, give us a call or come and see us at the
                store. Our team will help you find exactly what you need.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={STORE.phoneHref}
                  className={`${btnPrimary} ${btnMd}`}
                >
                  <PhoneCall size={18} aria-hidden="true" />
                  Call {STORE.phone}
                </a>
                <a
                  href={STORE.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${btnSecondary} ${btnMd} shadow-soft`}
                >
                  <Map size={18} aria-hidden="true" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SupportPage;
